/**
 * Platform admin (Greg-only): the org-creation invite surface for the S3
 * onboarding pipeline.
 *
 *   - Mint: a form that POSTs /admin/org-invites (maxUses, optional label,
 *     ttlDays). On success the one-time `invite_code` is shown ONCE — these are
 *     NATIVE penseur-trading org-creation invites (an `oci_…` DynamoDB row), NOT
 *     Guardius invites, so there's no `?invite=` SPA redeem link; the code is
 *     handed to the prospective org owner directly.
 *   - Invites: GET /admin/org-invites → outstanding invites (uses / expiry),
 *     each with a Revoke button (POST /admin/org-invites/{code}/revoke).
 *
 * Styling is the shared console design system only — surfaces (.card), section
 * titles (.section-title), tables (.detail-table), controls (.input), buttons
 * (.btn / .btn-save / .btn-pool-remove), notes/errors (.note / .error), and the
 * layout-only `team-*` classes reused from the Team tab. This tab invents no
 * visual design of its own.
 *
 *   - Requests (Task c.2): GET /admin/org-requests?status=pending → inbound
 *     org-creation requests staged by the onboarding wizard (b.1). Each row is
 *     reviewed and Approved (POST .../approve → async provisioning) or Rejected
 *     (POST .../reject → tears down staged secrets + CSV). The list/detail routes
 *     ALWAYS redact the row, so no Alpaca credential is ever present here — only
 *     the non-secret `alpaca_account_id` is shown. A decision note is optional.
 *
 * The tab is platform-admin-gated at the App level (only listed/routable for a
 * platform admin); it also guards internally as belt-and-suspenders, and gates
 * its data fetch on `isPlatformAdmin` so a non-admin never issues the admin read.
 */

import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import {
  useOrgInvites,
  useMintOrgInvite,
  useRevokeOrgInvite,
  useOrgRequests,
  useApproveOrgRequest,
  useRejectOrgRequest,
  type OrgInvite,
  type OrgRequest,
} from '../lib/queries';
import { ApiError } from '../lib/api';

const DEFAULT_TTL_DAYS = 30;

function errMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'unknown error';
}

function OrgInvitesList({ enabled }: { enabled: boolean }) {
  const { data, isLoading, isError, error } = useOrgInvites(enabled);
  const revoke = useRevokeOrgInvite();

  if (isLoading) return <p className="note">Loading invites…</p>;
  if (isError) {
    return (
      <p className="error" role="alert">
        Failed to load invites: {errMessage(error)}
      </p>
    );
  }

  // Revoked invites stay in the table with `status:'revoked'`. They're dead —
  // drop them so the "outstanding" table only shows live invites (otherwise a
  // successful revoke leaves the row sitting there, reading as a no-op).
  const invites: OrgInvite[] = (data?.invites ?? []).filter((inv) => inv.status !== 'revoked');
  if (invites.length === 0) {
    return <p className="note">No outstanding invites.</p>;
  }

  return (
    <>
      {revoke.isError && (
        <p className="error" role="alert">
          Failed to revoke invite: {errMessage(revoke.error)}
        </p>
      )}
      <table className="detail-table">
        <thead>
          <tr>
            <th>Code</th>
            <th>Uses</th>
            <th>Expires</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {invites.map((inv) => {
            const used = inv.use_count ?? 0;
            const max = inv.max_uses ?? '∞';
            // expires_at is epoch SECONDS.
            const expires =
              typeof inv.expires_at === 'number'
                ? new Date(inv.expires_at * 1000).toISOString()
                : '—';
            return (
              <tr key={inv.invite_code} data-invite-code={inv.invite_code}>
                <td title={inv.label}>{inv.invite_code}</td>
                <td>
                  {used} / {max}
                </td>
                <td>{expires}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-pool-remove"
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate(inv.invite_code)}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

function MintForm() {
  const mint = useMintOrgInvite();
  const [maxUses, setMaxUses] = useState(1);
  const [ttlDays, setTtlDays] = useState(DEFAULT_TTL_DAYS);
  const [label, setLabel] = useState('');
  // Which field was last copied ('link' | 'code'), for the "Copied!" confirm;
  // null = nothing copied (or the copy failed). Mirrors TeamTab's MintForm.
  const [copied, setCopied] = useState<'link' | 'code' | null>(null);
  const [copyFailed, setCopyFailed] = useState(false);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mint.isPending) return;
    mint.mutate({
      maxUses,
      ttlDays,
      ...(label.trim() ? { label: label.trim() } : {}),
    });
  };

  const minted = mint.data;
  // The org-creation invite is redeemed by the ONBOARDING WIZARD (a separate SPA
  // entry at /dashboard/onboarding.html), NOT the console's `?invite=` redeem
  // flow (which handles Guardius `gi_…` invites on the console root). Build the
  // link against the wizard entry so the prospective owner lands on onboarding.
  const shareUrl = minted
    ? `${window.location.origin}/dashboard/onboarding.html?invite=${minted.invite_code}`
    : '';

  // A fresh mint resets any prior "Copied!" confirmation.
  useEffect(() => {
    setCopied(null);
    setCopyFailed(false);
  }, [minted?.invite_code]);

  // Copy the link/code to the clipboard. The raw value stays visible above as a
  // manual fallback, so a clipboard failure (e.g. insecure context) is surfaced,
  // not swallowed — the user can still select the text by hand.
  const copy = async (which: 'link' | 'code', text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFailed(false);
      setCopied(which);
    } catch {
      setCopied(null);
      setCopyFailed(true);
    }
  };

  return (
    <form className="team-mint-form" onSubmit={onSubmit}>
      <label className="team-field">
        <span>Max uses</span>
        <input
          className="input"
          type="number"
          min={1}
          value={maxUses}
          onChange={(e) => setMaxUses(Math.max(1, Number(e.target.value) || 1))}
          aria-label="Max uses"
        />
      </label>

      <label className="team-field">
        <span>TTL (days)</span>
        <input
          className="input"
          type="number"
          min={1}
          value={ttlDays}
          onChange={(e) => setTtlDays(Math.max(1, Number(e.target.value) || 1))}
          aria-label="TTL (days)"
        />
      </label>

      <label className="team-field">
        <span>Label (optional)</span>
        <input
          className="input"
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          aria-label="Label"
        />
      </label>

      <button type="submit" className="btn btn-save team-mint-submit" disabled={mint.isPending}>
        {mint.isPending ? 'Minting…' : 'Mint invite'}
      </button>

      {mint.isError && (
        <p className="error" role="alert">
          Failed to mint invite: {errMessage(mint.error)}
        </p>
      )}

      {minted && (
        <div className="team-mint-result" role="status">
          <p className="note">
            This code is shown once — copy it now. It won&apos;t be shown again.
          </p>
          <p className="team-mint-code">
            <span className="team-mint-label">Code</span>
            <code data-testid="mint-code">{minted.invite_code}</code>
          </p>
          <p className="team-mint-link">
            <span className="team-mint-label">Onboarding link</span>
            <a href={shareUrl} data-testid="mint-share-url">
              {shareUrl}
            </a>
          </p>
          <div className="team-mint-actions">
            <button type="button" className="btn" onClick={() => copy('link', shareUrl)}>
              {copied === 'link' ? 'Copied!' : 'Copy link'}
            </button>
            <button type="button" className="btn" onClick={() => copy('code', minted.invite_code)}>
              {copied === 'code' ? 'Copied!' : 'Copy code'}
            </button>
          </div>
          {copyFailed && (
            <p className="error" role="alert">
              Couldn&apos;t copy to the clipboard — select the link above to copy it manually.
            </p>
          )}
        </div>
      )}
    </form>
  );
}

function RequestsPanel({ enabled }: { enabled: boolean }) {
  const { data, isLoading, isError, error } = useOrgRequests('pending', enabled);
  const approve = useApproveOrgRequest();
  const reject = useRejectOrgRequest();
  // The single decision note shared by Approve/Reject for the selected request.
  // Closed-beta volume is one-at-a-time review, so a per-panel note (not per-row)
  // is enough; it's cleared on a fresh decision.
  const [note, setNote] = useState('');

  if (isLoading) return <p className="note">Loading requests…</p>;
  if (isError) {
    return (
      <p className="error" role="alert">
        Failed to load requests: {errMessage(error)}
      </p>
    );
  }

  const requests: OrgRequest[] = data?.requests ?? [];

  const decide = (mutate: (input: { id: string; note?: string }) => void, id: string) => {
    const trimmed = note.trim();
    mutate({ id, ...(trimmed ? { note: trimmed } : {}) });
    setNote('');
  };

  return (
    <>
      {approve.isError && (
        <p className="error" role="alert">
          Failed to approve request: {errMessage(approve.error)}
        </p>
      )}
      {reject.isError && (
        <p className="error" role="alert">
          Failed to reject request: {errMessage(reject.error)}
        </p>
      )}
      {approve.data && (
        <p className="note" role="status">
          Approved — provisioning runs asynchronously. Poll this list; the request
          leaves “pending” once its org is provisioned.
        </p>
      )}

      <label className="team-field">
        <span>Decision note (optional)</span>
        <input
          className="input"
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          aria-label="Decision note"
        />
      </label>

      {requests.length === 0 ? (
        <p className="note">No pending requests.</p>
      ) : (
        // The row is RENDERED FIELD-BY-FIELD (org name / owner / account id /
        // created), never spread — a redacted row carries no secret, and an
        // explicit column list means a future stray field can't leak into the UI.
        <table className="detail-table">
          <thead>
            <tr>
              <th>Org</th>
              <th>Owner</th>
              <th>Alpaca account</th>
              <th>Requested</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {requests.map((req) => (
              <tr key={req.request_id} data-request-id={req.request_id}>
                <td>{req.org_name}</td>
                <td title={req.owner_identity_id}>
                  {req.owner_wallet ?? req.owner_identity_id}
                </td>
                <td>{req.alpaca_account_id}</td>
                <td>{req.created_at}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-save"
                    disabled={approve.isPending}
                    onClick={() => decide(approve.mutate, req.request_id)}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="btn btn-pool-remove"
                    disabled={reject.isPending}
                    onClick={() => decide(reject.mutate, req.request_id)}
                  >
                    Reject
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

export default function AdminTab() {
  const { isPlatformAdmin } = useAuth();

  // Belt-and-suspenders: the App only lists/routes this tab for platform admins,
  // but a non-admin who reaches it must see nothing actionable and issue no reads.
  if (!isPlatformAdmin) {
    return (
      <p className="error" role="alert">
        Platform admin only.
      </p>
    );
  }

  return (
    <div className="team-tab">
      <section className="card">
        <h3 className="section-title">Mint an org-creation invite</h3>
        <MintForm />
      </section>

      <section className="card">
        <h3 className="section-title">Outstanding invites</h3>
        <OrgInvitesList enabled={isPlatformAdmin} />
      </section>

      <section className="card">
        <h3 className="section-title">Pending org requests</h3>
        <RequestsPanel enabled={isPlatformAdmin} />
      </section>
    </div>
  );
}
